import { type FormInstance, message } from 'antd';
import moment from 'moment';
import type { KeyedMutator } from 'swr';

import type { ErrorResponse } from '@/utils/asyncErrorHandler';

import { CYCLE_TYPE_OPTIONS } from '../../constants';
import type { InspectionTableResponse, RecordInTable } from '../../types';
import type {
  EditRouteFormRequest,
  FormOptions,
  FormValues,
  SelectorFieldProps,
} from './types';

export const getFormFieldsValue = (editingRoute: RecordInTable) => {
  const equipments = editingRoute.equipment.map((item) => item.id);
  const inspectors = editingRoute.inspector.map((person) => person.id);
  const cycleTypeOption = CYCLE_TYPE_OPTIONS.find(
    (option) => option.value === editingRoute.cycle.type
  );
  const momentRanges = editingRoute.slot.map((timeRange) => {
    const [start, end] = timeRange.split('-');
    const momentRange = [moment(start, 'HH:mm'), moment(end, 'HH:mm')];
    return momentRange;
  });

  const newFieldsValue: FormValues = {
    ...editingRoute,
    equipment: equipments,
    inspector: inspectors,
    period: [
      moment(editingRoute.period.start),
      moment(editingRoute.period.end),
    ],
    cycle: {
      type: cycleTypeOption
        ? cycleTypeOption.value
        : CYCLE_TYPE_OPTIONS[0].value,
      days: editingRoute.cycle.days,
    },
    slot: momentRanges,
  };
  return newFieldsValue;
};

const generateFormRequestData = (formValues: FormValues) => {
  const start = formValues.period[0].startOf('day').valueOf();
  const end = formValues.period[1].endOf('day').valueOf();

  const days = [...formValues.cycle.days];
  days.sort((a, b) => a - b);

  formValues.slot.sort((a, b) => {
    const [aStart] = a;
    const [bStart] = b;
    return aStart.valueOf() - bStart.valueOf();
  });
  const slot = formValues.slot.map(([start, end]) => {
    const startTime = start.format('HH:mm');
    const endTime = end.format('HH:mm');
    return `${startTime}-${endTime}`;
  });

  const requestData: EditRouteFormRequest = {
    ...formValues,
    period: {
      start,
      end,
    },
    cycle: {
      ...formValues.cycle,
      days,
    },
    slot,
  };
  return requestData;
};

export const fetchFormOptions = async (apiUrl: string) => {
  try {
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        authorization: localStorage.getItem('feathers-jwt') ?? '',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data: FormOptions = await response.json();
    return data;
  } catch (error) {
    console.error('fetchFormOptions ', error);
    return undefined;
  }
};

const handleError = async (arg: {
  response: Response;
  setFields: FormInstance['setFields'];
  onClose: () => void;
}) => {
  const { response, setFields, onClose } = arg;
  try {
    const errorObj: ErrorResponse = await response.json();
    if (response.status === 409) {
      setFields([
        {
          name: 'route',
          errors: ['路線名稱已存在'],
        },
      ]);
    } else {
      const consoleError = `Error:\n\nStatus code: ${response.status}\n\nDebug info: ${errorObj.debugInfo}\n\nError message: ${errorObj.displayMessage}`;
      console.error(consoleError);
      message.error('資料提交失敗!');
      onClose();
    }
  } catch (error) {
    console.error(error);
    message.error('資料提交失敗!');
    onClose();
  }
};

export const onFinish = async (arg: {
  formValues: FormValues;
  formType: 'add' | 'edit';
  setFields: FormInstance['setFields'];
  onClose: () => void;
  revalidateRouteTableData: KeyedMutator<InspectionTableResponse | undefined>;
}) => {
  const { formValues, formType, setFields, onClose, revalidateRouteTableData } =
    arg;
  const requestData = generateFormRequestData(formValues);

  const apiUrl =
    formType === 'edit'
      ? `/ezcon/api/machineInspectionPlans/${requestData.id}`
      : '/ezcon/api/machineInspectionPlans';
  const method = formType === 'edit' ? 'PATCH' : 'POST';

  try {
    const response = await fetch(apiUrl, {
      method,
      body: JSON.stringify(requestData),
      headers: {
        'Content-Type': 'application/json',
        authorization: localStorage.getItem('feathers-jwt') ?? '',
      },
    });

    if (response.ok) {
      message.success('資料提交成功!');
      onClose();
      revalidateRouteTableData();
      return;
    }

    await handleError({
      response,
      setFields,
      onClose,
    });
  } catch (error) {
    console.error(error);
    message.error('資料提交失敗!');
    onClose();
  }
};

export const getSelectorFieldProps = (formOptions: FormOptions | undefined) => {
  const selectorFields: SelectorFieldProps[] = [
    {
      label: '設備',
      name: 'equipment',
      rules: [{ required: true, message: '請選擇設備', type: 'array' }],
      mode: 'multiple',
      placeholder: '請選擇設備',
      options: formOptions ? formOptions.equipments : [],
    },
    {
      label: '巡檢人員',
      name: 'inspector',
      rules: [{ required: true, message: '請選擇人員', type: 'array' }],
      mode: 'multiple',
      placeholder: '請選擇人員',
      options: formOptions ? formOptions.inspectors : [],
    },
  ];
  return selectorFields;
};
